/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Global cross-app pending-play sync.
 *
 * Each practice app has its OWN redux store persisted at
 * `persist:<app>-root`, and its CurrentGameSync only runs while that app is
 * mounted — so plays tagged offline in an app the coach never re-opens never
 * upload. This module lets the DASHBOARD upload EVERY app's pending plays from
 * one place, WITHOUT mounting each app.
 *
 * Safety by construction (see the sweeper's caller GlobalPendingSync.tsx):
 *   - runs ONLY when no embedded app is mounted (no other code is dispatching
 *     to a live store),
 *   - is strictly serialized (concurrent callers await the SAME in-flight run),
 *   - reads each app's persisted plays but NEVER writes any persist blob — the
 *     `play_id`-keyed upsert is idempotent, so the app's own CurrentGameSync
 *     harmlessly clears `needsSync` on its next visit,
 *   - routes each request to the right app via a PER-REQUEST `x-app-selected`
 *     header (never by mutating the shared global app key), so a concurrent
 *     writer of that global — e.g. the dashboard setting it to "Dashboard" on
 *     reconnect — can't misroute an app's uploads to the wrong bucket.
 */
import type { AxiosRequestConfig } from "axios";
import libraryServices from "@workspace/shared/core/services/library/library.services";
import { createPendingStore } from "@workspace/shared/core/utils/pendingSaves";
import { completeSettingsPayload, type QueuedSettings } from "@workspace/shared/core/utils/settingsPayload";
import { api } from "@workspace/shared/core/services/api";
import libraryRoutes from "@workspace/shared/core/services/library/library.routes";
import {
  pickCurrentGameCandidate,
  isServerBackedId,
  sanitizePlayForUpload,
} from "@workspace/shared/core/store/games.helpers";
import type { SavedPlay } from "@workspace/shared/core/store/savedPlays.factory";

// Every app that persists a `plays` slice. All 12 do (each whitelists "plays").
// The SHORT key ("wr") is used for the persist blob (persist:wr-root) and the
// offline queue (createPendingStore("wr")).
export const PLAYS_APPS = [
  "qb", "st", "wr", "db", "dc", "dl", "lb", "rb", "s", "te", "ol", "def",
] as const;

// The backend segregates each app's plays/games by the `x-app-selected` header,
// whose value is the app's CANONICAL name (what the dashboard sets on open via
// APP_KEY_BASE_PATHS) — NOT the short key. The sweeper must send the SAME
// canonical name the app itself sends, or plays would land under the wrong app.
const APP_SELECTED_KEY: Record<string, string> = {
  qb: "QB",
  st: "ST",
  wr: "WideReceiver",
  db: "DefensiveBack",
  dc: "DefensiveCoordinator",
  dl: "DL",
  lb: "LineBacker",
  rb: "RunningBack",
  s: "Safety",
  te: "TightEnd",
  ol: "OL",
  def: "DefLineMan",
};

type PersistedPlays = { plays?: SavedPlay[] } | null;
type PersistedGames = {
  activeGameId?: string | null;
  games?: Array<{ game_id?: string | number; complete?: boolean }>;
} | null;

function readSlice(appKey: string): { plays: PersistedPlays; games: PersistedGames } {
  try {
    const raw = localStorage.getItem(`persist:${appKey}-root`);
    if (!raw) return { plays: null, games: null };
    const root = JSON.parse(raw) as Record<string, string>;
    const parse = (s?: string) => {
      try {
        return s ? JSON.parse(s) : null;
      } catch {
        return null;
      }
    };
    return { plays: parse(root.plays), games: parse(root.games) };
  } catch {
    return { plays: null, games: null };
  }
}

/** True if `key` holds a JSON array with at least one element. */
function hasNonEmptyArray(key: string): boolean {
  const raw = localStorage.getItem(key);
  if (!raw) return false;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length > 0;
  } catch {
    return false;
  }
}

/**
 * The ONE current-game resolver: reuse a server-backed activeGameId, else adopt
 * the server's current game, else create one. Only reached when the caller has
 * confirmed there are unsynced plays to attach. `config` carries the per-request
 * `x-app-selected` header so the read/create hit the correct app's bucket.
 */
export async function resolveCurrentGameId(
  activeGameId?: string | null,
  config?: AxiosRequestConfig,
): Promise<string | null> {
  if (activeGameId && isServerBackedId(activeGameId)) return String(activeGameId);
  const { data } = await libraryServices.onGetGames(config);
  const existing = pickCurrentGameCandidate(Array.isArray(data) ? (data as any[]) : []);
  if (existing?.game_id) return String(existing.game_id);
  const { data: created } = await libraryServices.onSaveGame(
    {
      game_description: "Current Game",
      complete: false,
    } as any,
    config,
  );
  const id = (created as any)?.game_id ?? (created as any)?.gameId;
  return id != null ? String(id) : null;
}

/**
 * Drain qb/st OFFLINE play-cell EDITS queued at `${appKey}_pendingUpdatedPlays`
 * (only qb/st ever write this key). Each queued entry is already an onSavePlays
 * payload `{ game_id, plays: [{ play_id, play_data }] }`. Without this, those
 * edits are neither swept nor covered by the offline-logout warning, so a
 * logout wipes them. Leaves the key intact on failure so it retries later.
 */
async function flushPendingUpdatedPlays(
  appKey: string,
  config: AxiosRequestConfig,
): Promise<void> {
  let groups: any[];
  try {
    const raw = localStorage.getItem(`${appKey}_pendingUpdatedPlays`);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    groups = Array.isArray(parsed) ? parsed : [];
  } catch {
    return;
  }
  if (groups.length === 0) return;
  for (const group of groups) {
    const gameId = group?.game_id;
    const plays = Array.isArray(group?.plays) ? group.plays : [];
    if (!gameId || plays.length === 0) continue;
    try {
      await libraryServices.onSavePlays({ game_id: gameId, plays } as any, config);
    } catch (e) {
      console.warn(`[GlobalPendingSync] ${appKey} updated-plays flush failed`, e);
      return; // keep the key so it can retry; don't drop the edits
    }
  }
  localStorage.removeItem(`${appKey}_pendingUpdatedPlays`);
}

/** Upload one app's unsynced plays + drain its offline queues. Never writes persist. */
export async function flushAppPendingPlays(appKey: string): Promise<void> {
  const { plays, games } = readSlice(appKey);
  const list: SavedPlay[] = plays?.plays ?? [];
  // MUST match syncCurrentGamePlaysToBackend's filter + grouping exactly.
  const unsynced = list.filter(
    (p) => (p.needsSync !== false || !p.game_id) && !p.isDeleted,
  );

  // Route every request this flush issues to THIS app by stamping the canonical
  // x-app-selected header PER REQUEST (see api.ts interceptor). We no longer
  // mutate the shared global app key: a concurrent writer could otherwise
  // clobber it mid-flush and land plays under the wrong app.
  const selectedKey = APP_SELECTED_KEY[appKey] ?? appKey;
  const cfg: AxiosRequestConfig = { headers: { "x-app-selected": selectedKey } };

  // A game saved offline goes up first: until it has, it is the server's
  // current game, and plays tagged since would attach to it. While it can't,
  // they wait (the check below then reports this app as not flushed).
  if (hasNonEmptyArray(`${appKey}_pendingGames`)) {
    await createPendingStore(appKey).syncPending(cfg);
  }
  const saveStillQueued = hasNonEmptyArray(`${appKey}_pendingGames`);

  if (unsynced.length > 0 && !saveStillQueued) {
    // Same split as syncCurrentGamePlaysToBackend: plays pinned to a server
    // game upload to THAT game; only plays without one attach to the current
    // game. Posting the whole batch to the current game re-homed previous
    // games' edits AND hit the backend's cross-game 400, wedging the sweep.
    const toUpload = (p: SavedPlay) => ({
      // NEVER mint a new id — that would duplicate on the app's next upload.
      play_id: p.play_id ?? p.id,
      play_data: sanitizePlayForUpload(p),
    });
    const currentBucket = unsynced.filter((p) => !isServerBackedId(p.game_id));
    const byGame = new Map<string, SavedPlay[]>();
    for (const p of unsynced) {
      if (!isServerBackedId(p.game_id)) continue;
      const gid = String(p.game_id);
      const bucket = byGame.get(gid) ?? [];
      bucket.push(p);
      byGame.set(gid, bucket);
    }

    if (currentBucket.length > 0) {
      // The persisted games list contains ONLY completed games (the persist
      // transform strips the rest) — so if activeGameId appears in it, that
      // game was finished elsewhere and must not receive new plays.
      const activeIsCompleted = (games?.games ?? []).some(
        (g) =>
          String(g?.game_id ?? "") === String(games?.activeGameId ?? "") &&
          g?.complete === true,
      );
      const gameId = await resolveCurrentGameId(
        activeIsCompleted ? null : games?.activeGameId,
        cfg,
      );
      if (gameId) {
        await libraryServices.onSavePlays(
          { game_id: gameId, plays: currentBucket.map(toUpload) } as any,
          cfg,
        );
      }
    }
    for (const [gid, bucket] of byGame) {
      await libraryServices.onSavePlays(
        { game_id: gid, plays: bucket.map(toUpload) } as any,
        cfg,
      );
    }
  }
  // Drain queued offline Save-Game + queued DELETEs (offline-delete Plan 1)
  // from the dashboard too. syncPending orders settings→games→plays→deletes,
  // drops 404/403. Same per-request app header.
  await createPendingStore(appKey).syncPending(cfg);
  // Drain qb/st offline play-cell edits (no-op for the other 10 apps).
  await flushPendingUpdatedPlays(appKey, cfg);
  // Drain the app-scoped SETTINGS queue (`<app>_pendingSaves`, written by the
  // fields slices when a settings save can't reach the server). App-scoped =
  // safe to route with this app's x-app-selected header; the LEGACY shared
  // "pendingSaves" key has no app attribution and is deliberately NOT drained
  // here (the logout guard warns about it instead).
  await flushPendingSettingsSaves(appKey, cfg);

  // syncPending and flushPendingUpdatedPlays deliberately swallow upload
  // errors internally (they keep their queues for a later retry and return/
  // resolve normally). For the LOGOUT guard that silence is fatal: a fully
  // failed drain would read as success, flushedOk would stay true, and the
  // sign-out wipe would destroy the still-queued games/plays/edits. So after
  // draining, any queue that is still non-empty means this app did NOT flush.
  // "Days": games still to be moved to the day their coach confirmed.
  for (const kind of ["Settings", "Games", "Plays", "Deletes", "UpdatedPlays", "Saves", "Days"]) {
    if (hasNonEmptyArray(`${appKey}_pending${kind}`)) {
      throw new Error(
        `[GlobalPendingSync] ${appKey} still has queued pending${kind} after drain`,
      );
    }
  }
}

/**
 * Upload one app's queued Settings saves now (in-app, on reconnect and every
 * sync tick): they used to wait for the next app load. Failures keep the
 * queue for a later try.
 */
export async function flushAppSettingsSaves(appKey: string): Promise<void> {
  const selectedKey = APP_SELECTED_KEY[appKey] ?? appKey;
  await flushPendingSettingsSaves(appKey, { headers: { "x-app-selected": selectedKey } });
}

/**
 * Drain `<app>_pendingSaves` — offline/failed SETTINGS saves queued by the
 * app's fields slice. Uploaded in order; on failure the remainder stays for a
 * later retry (the post-drain check above then reports the app as not
 * flushed, so a logout can't silently wipe it).
 */
async function flushPendingSettingsSaves(
  appKey: string,
  config: AxiosRequestConfig,
): Promise<void> {
  let queue: unknown[];
  try {
    const readArr = (k: string): unknown[] => {
      const parsed = JSON.parse(localStorage.getItem(k) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    };
    // Migrate the app's older wrapper-prefixed queue ("<app>-pendingSaves",
    // app-scoped so safe to claim) into the canonical key first.
    const legacy = readArr(`${appKey}-pendingSaves`);
    if (legacy.length > 0) {
      localStorage.setItem(
        `${appKey}_pendingSaves`,
        JSON.stringify([...legacy, ...readArr(`${appKey}_pendingSaves`)]),
      );
      localStorage.removeItem(`${appKey}-pendingSaves`);
    }
    queue = readArr(`${appKey}_pendingSaves`);
    if (queue.length === 0) return;
  } catch {
    return;
  }
  while (queue.length > 0) {
    try {
      // What the device didn't know goes back as the server has it: the
      // server keeps the whole record a save sends.
      const payload = await completeSettingsPayload(queue[0] as QueuedSettings, async () => {
        const { data } = await api.get(libraryRoutes.settingsRoute, config);
        return (data as { settings?: { fields?: QueuedSettings["fields"] } } | null)?.settings?.fields;
      });
      await libraryServices.onSaveSettings(payload as any, config);
    } catch (e) {
      console.warn(`[GlobalPendingSync] ${appKey} settings flush failed`, e);
      localStorage.setItem(`${appKey}_pendingSaves`, JSON.stringify(queue));
      return; // keep the remainder; post-drain check flags the failure
    }
    queue = queue.slice(1);
    localStorage.setItem(`${appKey}_pendingSaves`, JSON.stringify(queue));
  }
}

/**
 * True if ANY app has plays that still need uploading — either unsynced plays
 * in its persisted redux buffer, or items queued in its offline save queues.
 * Used to warn on an OFFLINE logout (which can't upload) that data would be lost.
 */
export function hasAnyPendingUpload(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    for (const appKey of PLAYS_APPS) {
      const { plays } = readSlice(appKey);
      const list: SavedPlay[] = plays?.plays ?? [];
      if (list.some((p) => (p.needsSync !== false || !p.game_id) && !p.isDeleted)) {
        return true;
      }
      // Offline queues: <app>_pending{Games,Plays,Deletes,Settings,UpdatedPlays,Saves,Days}.
      // UpdatedPlays = qb/st offline play-cell edits (only they write it).
      // Saves = the fields slices' queued settings saves; the dash variant is
      // the older wrapper-prefixed location of the same queue.
      for (const kind of ["Games", "Plays", "Deletes", "Settings", "UpdatedPlays", "Saves", "Days"]) {
        if (hasNonEmptyArray(`${appKey}_pending${kind}`)) return true;
      }
      if (hasNonEmptyArray(`${appKey}-pendingSaves`)) return true;
    }
    // LEGACY / unprefixed queues from older builds and the still-live
    // "pendingSaves" offline-settings queue that 8 apps (db/dc/dl/lb/te/s/def/ol)
    // write via their fields slice. The dashboard logout clears ALL of these
    // (incl. "pendingSaves") but the sweep's per-app drain only covers the
    // prefixed keys, so an offline logout would wipe them without warning unless
    // we check them here directly.
    for (const legacyKey of [
      "pendingGames",
      "pendingPlays",
      "pendingSettings",
      "pendingSaves",
    ]) {
      if (hasNonEmptyArray(legacyKey)) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

// Holds the in-flight sweep so concurrent callers AWAIT THE SAME run instead of
// getting a resolved no-op. A logout that coincides with the 60s background
// sweep must actually wait for it to finish — else logout clears storage before
// un-swept apps upload (permanent data loss).
let sweeping: Promise<boolean> | null = null;

/**
 * Serially flush every app's pending plays + queues. Safe to call repeatedly.
 * Resolves true only when EVERY app flushed without error — a false return
 * means some pending data is still stuck on this device (dead auth, server
 * error, …) and a sign-out wipe would lose it.
 */
export function sweepAllPendingApps(): Promise<boolean> {
  if (sweeping) return sweeping;
  sweeping = (async () => {
    let allOk = true;
    try {
      for (const appKey of PLAYS_APPS) {
        try {
          await flushAppPendingPlays(appKey);
        } catch (e) {
          allOk = false;
          console.warn(`[GlobalPendingSync] ${appKey} flush failed`, e);
        }
      }
    } finally {
      sweeping = null;
    }
    return allOk;
  })();
  return sweeping;
}

/**
 * Logout-safe flush. Await any in-flight sweep, THEN run one guaranteed fresh
 * pass. The first await may join a sweep that had already passed some apps
 * before this call; the second pass (sweeping is null again) re-reads current
 * storage so a straggler that gained pending data is still uploaded before
 * logout wipes persist:* / *_pending*. Callers must await this BEFORE clearing
 * storage on sign-out, and must NOT wipe when it resolves false while
 * hasAnyPendingUpload() is true — that combination means unuploaded coach data
 * would be destroyed.
 */
export async function flushAllPendingAppsForLogout(): Promise<boolean> {
  try {
    await sweepAllPendingApps();
  } catch {
    /* ignore — the authoritative fresh pass below reports the outcome */
  }
  let ok: boolean;
  try {
    ok = await sweepAllPendingApps();
  } catch {
    ok = false;
  }
  // The legacy unprefixed "pendingSaves" settings queue (written by 8 apps
  // under ONE shared key with no app attribution) can only be uploaded by the
  // app that wrote it — the sweep cannot route it. If it still holds items,
  // this device has data a logout wipe would destroy: report failure so the
  // sign-out guard warns instead of silently wiping.
  if (ok && hasNonEmptyArray("pendingSaves")) {
    console.warn(
      "[GlobalPendingSync] unprefixed pendingSaves queue is non-empty and cannot be swept — blocking silent wipe",
    );
    ok = false;
  }
  return ok;
}
